import { useFormik } from "formik";
import React, { useState } from "react";
import Chart from "react-apexcharts";
import { HiFilter } from "react-icons/hi";
import { CreateAndDetailLayout } from "../../../components/CreateAndDetailLayout";
import { InputCheckboxV2 } from "../../../components/Form/InputCheckboxV2";
import { Select } from "../../../components/Form/Select";
import { InfoCards } from "../../../components/InfoCards";
import Modal from "../../../components/Modal";
import { PanelAndDetailAndCreateHeader } from "../../../components/panel/PanelAndDetailAndCreateHeader";
import { Pagination } from "../../../components/panel/table/Pagination";
import { TableContent } from "../../../components/panel/table/TableContent";
import { TableHeader } from "../../../components/panel/table/TableHeader";
import {
  RegisterSeller,
  useRegisterSellers,
} from "../../../hook/queries/useRegisterSeller";
import {
  RegisterSellerFiltersAnalytic,
  useRegisterSellersAnalytic,
} from "../../../hook/queries/useRegisterSellerAnalytic";
import { ButtunSubmit } from "../../../styles/global";
import { columnsRegisterSeller } from "../../panel/PanelRegisterSellers";
import {
  BoxAnalytic,
  BoxFilter,
  ContainerBoxAnalytic,
  Form,
  ModalBody,
} from "../styles";

interface IProps {
  admin: boolean;
}

const APP_LINK =
  process.env.NODE_ENV !== "development"
    ? "http://portal.alpardobrasil.com.br"
    : "http://localhost:3000";

const RegisterSellerAnalytic: React.FC<IProps> = ({ admin }) => {
  const [page, setPage] = useState<number>(1);

  const [modalVisible, setModalVisible] = useState(false);
  const [filters, setFilters] = useState<RegisterSellerFiltersAnalytic>({});

  const getRegisterSellersAnalytic = useRegisterSellersAnalytic(filters);
  const getRegisterSellers = useRegisterSellers(page, filters, 15);

  const formikFilter = useFormik({
    initialValues: {
      brandsCodes: [],
      statusNames: [],
      isStatusPending: filters.isStatusPending,
    },
    onSubmit: (data) => {
      handleFilter(data);
    },
  });

  function handleFilter(filter: RegisterSellerFiltersAnalytic) {
    setFilters(filter);
    setModalVisible(false);
  }

  function clearFilter() {
    setModalVisible(false);
    setFilters({});
  }

  return (
    <CreateAndDetailLayout isLoading={getRegisterSellersAnalytic.isLoading}>
      <PanelAndDetailAndCreateHeader
        title={`Análise de cadastro representante`}
        goBack
      />

      <InfoCards
        cards={[
          {
            title: "QUANTIDADE TOTAL",
            value: getRegisterSellersAnalytic.data?.total ?? "-",
          },
        ]}
      />

      <ContainerBoxAnalytic>
        <BoxAnalytic>
          <h3>Marcas</h3>

          {getRegisterSellersAnalytic.data?.brands && (
            <div style={{ width: "100%" }}>
              <Chart
                type="donut"
                height={260}
                width="100%"
                series={getRegisterSellersAnalytic.data?.brands.map(
                  (item) => item.count
                )}
                options={{
                  series: getRegisterSellersAnalytic.data?.brands.map(
                    (item) => item.count
                  ),
                  labels: getRegisterSellersAnalytic.data?.brands.map(
                    (item) => item.name
                  ),
                }}
              />
            </div>
          )}
        </BoxAnalytic>
        <BoxAnalytic>
          <h3>Situações</h3>

          {getRegisterSellersAnalytic.data?.status && (
            <div style={{ width: "100%" }}>
              <Chart
                type="bar"
                height={260}
                width="100%"
                options={{
                  chart: {
                    toolbar: {
                      show: false,
                    },
                  },

                  plotOptions: {
                    bar: {
                      distributed: true,
                      borderRadius: 4,
                      horizontal: false,
                    },
                  },
                  dataLabels: {
                    enabled: true,
                  },
                  colors: getRegisterSellersAnalytic.data?.status.map(
                    (item) => item.color
                  ),
                  xaxis: {
                    categories: getRegisterSellersAnalytic.data?.status.map(
                      (item) => item.name
                    ),
                    labels: {
                      show: false,
                    },
                  },
                }}
                series={[
                  {
                    name: "Qtd",
                    data: getRegisterSellersAnalytic.data?.status.map(
                      (item) => item.count
                    ),
                  },
                ]}
              />
            </div>
          )}
        </BoxAnalytic>
      </ContainerBoxAnalytic>

      <TableHeader
        title={`REPRESENTANTES (${getRegisterSellers.data?.totalCount ?? 0})`}
        isFetching={
          getRegisterSellers.isFetching && !getRegisterSellers.isLoading
        }
      />

      <TableContent<RegisterSeller>
        data={getRegisterSellers.data?.registerSellers}
        columns={columnsRegisterSeller.filter((f) => f.viewList)}
        isLoading={getRegisterSellers.isLoading}
        onClickRow={(e) => {
          window.open(
            `${APP_LINK}/${
              admin ? "admin" : "representante"
            }/cadastro-representante/${e.id}`,
            "_blank"
          );
        }}
      />

      <Pagination
        totalCountOfRegisters={getRegisterSellers.data?.totalCount ?? 0}
        currentPage={page}
        onPageChange={setPage}
        registersPerPage={15}
      />

      <BoxFilter>
        <div className="fixedButton">
          <button onClick={() => setModalVisible(!modalVisible)}>
            <HiFilter color="#FFF" size={26} />
          </button>
        </div>
      </BoxFilter>

      {modalVisible && (
        <Modal
          title="Filtros"
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          maxHeight={475}
          scrollOff
        >
          <ModalBody>
            <Form onSubmit={formikFilter.handleSubmit}>
              <div
                style={{
                  overflowY: "auto",
                  height: 350,
                  maxHeight: 350,
                  padding: 10,
                }}
              >
                <Select
                  {...formikFilter.getFieldProps("isStatusPending")}
                  data={[
                    { name: "Sim", value: "1" },
                    { name: "Não", value: "01" },
                  ]}
                  label="Pendentes"
                />

                <div className="field-group">
                  <InputCheckboxV2
                    height="120px"
                    name="brandsCodes"
                    label="MARCA"
                    checks={formikFilter.values.brandsCodes}
                    onChange={(value) => {
                      formikFilter.setFieldValue("brandsCodes", value);
                    }}
                    data={
                      getRegisterSellersAnalytic?.data?.brands?.map((item) => ({
                        value: String(item.name),
                        description: item.name,
                      })) ?? []
                    }
                  />

                  <InputCheckboxV2
                    height="120px"
                    name="statusNames"
                    label="SITUAÇÃO"
                    checks={formikFilter.values.statusNames}
                    onChange={(value) => {
                      formikFilter.setFieldValue("statusNames", value);
                    }}
                    data={
                      getRegisterSellersAnalytic?.data?.status?.map((item) => ({
                        value: String(item.name),
                        description: item.name,
                      })) ?? []
                    }
                  />
                </div>
              </div>

              <div className="field-group">
                <div className="field" style={{ marginBottom: 0 }}>
                  <ButtunSubmit
                    onClick={clearFilter}
                    style={{
                      background: "#fff",
                      color: "#d21e26",
                    }}
                    type="button"
                  >
                    LIMPAR FILTRO
                  </ButtunSubmit>
                </div>

                <div className="field" style={{ marginBottom: 0 }}>
                  <ButtunSubmit type="submit">BUSCAR</ButtunSubmit>
                </div>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      )}
    </CreateAndDetailLayout>
  );
};

export { RegisterSellerAnalytic };
